import { useEffect, useMemo, useState } from 'react';

import { Logger } from '@rover/rsdk/src/modules/Logging';

import { fitWithinBounds, hasIntrinsicSize, ImageSize, NO_IMAGE_SIZE } from './useImageSize.common';

/** `fallbackSize` is the natural size to use when the photo can't be measured. */
const useImageSize = (
  uri: string,
  bounds: ImageSize,
  intrinsicSize?: ImageSize,
  fallbackSize?: ImageSize
): ImageSize => {
  const isKnown = hasIntrinsicSize(intrinsicSize);
  const { width: fallbackWidth, height: fallbackHeight } = fallbackSize ?? NO_IMAGE_SIZE;
  const [measuredSize, setMeasuredSize] = useState<ImageSize>(NO_IMAGE_SIZE);

  useEffect(() => {
    // Downloading the whole photo to read `naturalWidth` is the fallback for a
    // photo that arrives without dimensions: a local file picked for upload, and
    // the galleries whose images carry none — message attachments and the sitter
    // relationship gallery.
    if (isKnown || !uri) {
      setMeasuredSize(NO_IMAGE_SIZE);
      return undefined;
    }

    // A photo that finishes loading after its URI changed must not overwrite the new size.
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (!cancelled) setMeasuredSize({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      if (cancelled) return;
      setMeasuredSize({ width: fallbackWidth, height: fallbackHeight });
      Logger.warn('Failed to load image to measure its size', { action: 'getImageSize', uri });
    };
    img.src = uri;

    return () => {
      cancelled = true;
    };
  }, [isKnown, uri, fallbackWidth, fallbackHeight]);

  const { width: boundsWidth, height: boundsHeight } = bounds;

  return useMemo(() => {
    const naturalSize = isKnown && intrinsicSize ? intrinsicSize : measuredSize;
    if (!hasIntrinsicSize(naturalSize)) return NO_IMAGE_SIZE;

    return fitWithinBounds(naturalSize.width, naturalSize.height, {
      width: boundsWidth,
      height: boundsHeight,
    });
  }, [intrinsicSize, isKnown, measuredSize, boundsWidth, boundsHeight]);
};

export default useImageSize;
